/**
 * `/c/<slug>` — the clinic's entry URL (ZOY-614). It opens the app
 * PRESELECTED on that clinic; the slug is an alias only and grants nothing.
 *
 * The four honest outcomes:
 *  - unknown / malformed / reserved slug → not found (reserved and malformed
 *    are decided locally, with no request, and read the same as unknown);
 *  - signed out → remember the clinic and go to the branded sign-in card;
 *  - signed in AND a member → select the clinic and continue to the app
 *    (suspended membership and a deactivated clinic then render their own
 *    existing states — this page never decides authority);
 *  - signed in and NOT a member → access denied, naming the clinic, with the
 *    chooser over their real memberships and sign-out. Never a silent
 *    fallback into a different clinic.
 *
 * Nothing here reveals, before sign-in, whether the visitor has a membership.
 */

import { useEffect } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import s from '../access/access.module.css';
import { Button, Notice, Skeleton } from '@/ui/kit';
import { useSession } from '@/core/auth/SessionProvider';
import { fetchMeAuthz } from '@/core/authz/api';
import { clearStoredClinic, setSelectedClinicId, storeClinic } from '@/core/authz/clinicContext';
import { isValidSlug, resolveClinicSlug } from '@/core/clinic/entryApi';
import { clearEntry, rememberEntry } from './entryIntent';

function CenterBox({ children }: { children: React.ReactNode }) {
  return (
    <div className={s.page}>
      <div
        style={{
          inlineSize: 'min(480px, 100%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function ClinicEntryPage() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string>) => intl.formatMessage({ id }, v);
  const { slug } = useParams({ strict: false }) as { slug: string };
  const navigate = useNavigate();
  const { session, signOut } = useSession();
  const uid = session.status === 'signedIn' ? session.me.uid : null;

  // Reserved and malformed slugs never reach the network — and read exactly
  // like an unknown one, as the backend contract intends.
  const wellFormed = isValidSlug(slug);
  const entryQuery = useQuery({
    queryKey: ['clinic-entry', slug],
    queryFn: () => resolveClinicSlug(slug),
    enabled: wellFormed,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const entry = entryQuery.data ?? null;

  // The clinic travels with the tab: it brands the sign-in card and is where
  // a later sign-out returns to.
  useEffect(() => {
    if (entry) rememberEntry(entry);
  }, [entry]);

  // Signed out: the branded sign-in card, then back here.
  useEffect(() => {
    if (entry && session.status === 'signedOut') void navigate({ to: '/signin', replace: true });
  }, [entry, session.status, navigate]);

  // Signed in: the caller's OWN memberships decide. This read carries no
  // clinic header — it is the discovery endpoint, and a clinic the person
  // does not belong to must never be put on the wire as a selection.
  const authzQuery = useQuery({
    queryKey: ['entry-authz', uid],
    queryFn: () => fetchMeAuthz(),
    enabled: entry !== null && uid !== null,
    retry: 1,
  });
  const membership =
    entry && authzQuery.data
      ? (authzQuery.data.memberships.find((m) => m.clinicId === entry.clinicId) ?? null)
      : null;

  // A member: select the clinic and continue. Suspended membership and
  // deactivated clinic are NOT decided here — the app's own states render.
  useEffect(() => {
    if (!entry || uid === null || !authzQuery.data || membership === null) return;
    storeClinic(uid, entry.clinicId);
    setSelectedClinicId(entry.clinicId);
    void navigate({ to: '/home', replace: true });
  }, [entry, uid, authzQuery.data, membership, navigate]);

  if (!wellFormed || (entryQuery.isError && !entryQuery.isFetching)) {
    return (
      <CenterBox>
        <Notice kind="warn" title={t('entry.notFoundTitle')} role="alert">
          {t('entry.notFoundBody')}
        </Notice>
        <div>
          <Link to="/">
            <Button variant="brand">{t('entry.notFoundCta')}</Button>
          </Link>
        </div>
      </CenterBox>
    );
  }

  // Signed in, resolved, and NOT a member of this clinic.
  if (entry && uid !== null && authzQuery.data && membership === null) {
    return (
      <CenterBox>
        <Notice kind="err" title={t('entry.deniedTitle')} role="alert">
          {t('entry.deniedBody', { clinic: entry.displayName ?? entry.slug })}
        </Notice>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button
            variant="brand"
            onClick={() => {
              clearEntry();
              clearStoredClinic(uid);
              setSelectedClinicId(null);
              void navigate({ to: '/home', replace: true });
            }}
          >
            {t('entry.deniedChoose')}
          </Button>
          <Button
            variant="quiet"
            onClick={() => {
              clearEntry();
              void signOut().then(() => navigate({ to: '/signin', replace: true }));
            }}
          >
            {t('account.signOut')}
          </Button>
        </div>
      </CenterBox>
    );
  }

  if (authzQuery.isError) {
    return (
      <CenterBox>
        <Notice kind="warn" title={t('error.authzDownTitle')} role="alert">
          {t('error.authzDownBody')}
        </Notice>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="brand" onClick={() => void authzQuery.refetch()}>
            {t('app.retry')}
          </Button>
          <Button variant="quiet" onClick={() => void signOut()}>
            {t('account.signOut')}
          </Button>
        </div>
      </CenterBox>
    );
  }

  return (
    <CenterBox>
      <Skeleton height="20px" width="40%" />
      <Skeleton height="14px" />
      <Skeleton height="14px" width="80%" />
    </CenterBox>
  );
}
